import { SxProps, Theme } from "@mui/material";
import settingsShellTokens from "screens/Settings/Settings.tokens";
import rolesTokens, { rolesResponsiveTokens } from "./Roles.tokens";
import {
  PHONE_SM_QUERY,
  settingsShellResponsiveTokens,
} from "screens/Settings/Settings.tokens";

const t = rolesTokens;
const r = rolesResponsiveTokens;

/** Same rhythm `SettingsLayout` puts between its own stacked cards. */
const shellPhone = settingsShellResponsiveTokens.phone;
const stackGap = {
  xs: shellPhone.contentGap.std,
  sm: settingsShellResponsiveTokens.tablet.contentGap,
};

/** Page heading ("Roles" / "Create Role" / "Edit Role"). */
export const pageTitleSx: SxProps<Theme> = {
  color: settingsShellTokens.colors.pageTitle,
  fontSize: settingsShellTokens.pageTitle.fontSize,
  fontWeight: settingsShellTokens.pageTitle.fontWeight,
  letterSpacing: settingsShellTokens.pageTitle.letterSpacing,
  lineHeight: settingsShellTokens.pageTitle.lineHeight,
};

/** White rounded card that wraps the whole roles list. */
export const rolesCardSx: SxProps<Theme> = {
  /*
    Below `md` the reference drops the wrapper: every role is its own card and
    the pager sits loose underneath, straight on the page canvas.
  */
  bgcolor: { xs: "transparent", md: t.color.surface },
  border: {
    xs: "none",
    md: `${t.border.hairline} solid ${t.color.cardBorder}`,
  },
  borderRadius: { xs: 0, md: t.radius.card },
  boxShadow: { xs: "none", md: t.shadow.card },
  overflow: { xs: "visible", md: "hidden" },
  display: { xs: "flex", md: "block" },
  flexDirection: "column",
  gap: stackGap,
  [PHONE_SM_QUERY]: { gap: shellPhone.contentGap.sm },
};

/** Grey "ROLE NAME / RESOURCES AND PERMISSIONS" strip. */
export const columnHeaderSx: SxProps<Theme> = {
  // Two fixed tracks the phone cannot carry; the rows become cards below `md`,
  // so the strip goes with them.
  display: { xs: "none", md: "grid" },
  gridTemplateColumns: t.grid.row,
  px: t.space.gutter,
  py: t.space.columnHeaderY,
  bgcolor: t.color.headerBg,
  borderBottom: `${t.border.hairline} solid ${t.color.headerBorder}`,
  fontSize: t.font.columnHeader,
  fontWeight: t.weight.strong,
  letterSpacing: t.letterSpacing.columnHeader,
  color: t.color.columnHeader,
  lineHeight: "normal",
};

/**
 * A single role row. Rows holding more than one resource line top-align their
 * two columns; single-line rows centre them. The last row drops its divider.
 */
export const rowSx = (stacked: boolean, isLast: boolean): SxProps<Theme> => ({
  display: "grid",
  gridTemplateColumns: t.grid.row,
  alignItems: stacked ? "flex-start" : "center",
  px: t.space.gutter,
  py: t.space.rowY,
  borderBottom: isLast
    ? "none"
    : `${t.border.hairline} solid ${t.color.rowDivider}`,
});

const roleNameType = {
  fontSize: t.font.roleName,
  fontWeight: t.weight.strong,
  color: t.color.ink,
  lineHeight: "normal",
} as const;

/** Role name for the built-in roles that cannot be edited. */
export const roleNameSx = (stacked: boolean): SxProps<Theme> => ({
  ...roleNameType,
  pt: stacked ? t.space.nameOffsetTop : 0,
  minWidth: 0,
  pr: t.space.permissionGap,
  wordBreak: "break-word",
});

/**
 * Role name for editable roles. Rendered as a button so the edit flow stays
 * reachable, but stripped back to look exactly like the plain-text variant.
 * `font: inherit` must land before the type tokens — buttons do not inherit the
 * page font, and the shorthand would otherwise reset what the tokens set.
 */
export const roleNameButtonSx = (stacked: boolean): SxProps<Theme> => ({
  font: "inherit",
  p: 0,
  m: 0,
  border: "none",
  bgcolor: "transparent",
  ...roleNameType,
  pt: stacked ? t.space.nameOffsetTop : 0,
  pr: t.space.permissionGap,
  minWidth: 0,
  textAlign: "left",
  wordBreak: "break-word",
  cursor: "pointer",
  "&:hover": { color: t.color.accent },
  "&:focus-visible": {
    outline: `2px solid ${t.color.accent}`,
    outlineOffset: "2px",
    borderRadius: "3px",
  },
});

/** Column stacking one line per resource. */
export const resourceListSx: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  gap: t.space.resourceRowGap,
  minWidth: 0,
};

/** One resource: its label followed by the permission pills. */
export const resourceRowSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  gap: t.space.permissionGap,
  flexWrap: "wrap",
};

export const resourceLabelSx: SxProps<Theme> = {
  width: t.size.resourceLabelWidth,
  flexShrink: 0,
  fontSize: t.font.resourceLabel,
  fontWeight: t.weight.medium,
  color: t.color.resourceLabel,
  lineHeight: "normal",
  textTransform: "capitalize",
};

export const permissionPillSx: SxProps<Theme> = {
  display: "inline-flex",
  alignItems: "center",
  fontSize: t.font.permission,
  fontWeight: t.weight.medium,
  color: t.color.permissionText,
  bgcolor: t.color.permissionBg,
  px: t.space.permissionPaddingX,
  py: t.space.permissionPaddingY,
  borderRadius: t.radius.pill,
  lineHeight: "normal",
  whiteSpace: "nowrap",
};

/** Placeholder shown in place of rows when no role matches. */
export const emptyStateSx: SxProps<Theme> = {
  px: t.space.gutter,
  py: t.space.emptyY,
  textAlign: "center",
  fontSize: t.font.empty,
  fontWeight: t.weight.regular,
  color: t.color.emptyText,
  lineHeight: "normal",
};

/** Footer strip hosting the pager. */
export const paginationBarSx: SxProps<Theme> = {
  display: "flex",
  // The reference centres the pager under the cards rather than closing a card
  // with it, so below `md` the strip loses its rule and its gutters.
  justifyContent: { xs: "center", md: "flex-end" },
  alignItems: "center",
  px: { xs: 0, md: t.space.gutter },
  py: { xs: r.pager.paddingY, md: t.space.footerY },
  borderTop: {
    xs: "none",
    md: `${t.border.hairline} solid ${t.color.headerBorder}`,
  },
};

/** MUI <Pagination> restyled to the design's 28px square items. */
export const paginationSx: SxProps<Theme> = {
  "& .MuiPagination-ul": {
    gap: { xs: r.pager.gap, md: t.space.paginationGap },
    flexWrap: "nowrap",
  },
  "& .MuiPagination-ul > li": { m: 0 },
  "& .MuiPaginationItem-root": {
    minWidth: { xs: r.pager.itemSize, md: t.size.pageItem },
    width: { xs: r.pager.itemSize, md: t.size.pageItem },
    height: { xs: r.pager.itemSize, md: t.size.pageItem },
    m: 0,
    p: 0,
    borderRadius: { xs: r.pager.itemRadius, md: t.radius.pageItem },
    bgcolor: "transparent",
    // The reference outlines every idle page item below `md`.
    border: {
      xs: `${t.border.hairline} solid ${r.pager.idleBorder}`,
      md: "none",
    },
    fontSize: { xs: r.pager.fontSize, md: t.font.pageItem },
    fontWeight: t.weight.medium,
    color: t.color.pageItem,
    "&:hover": { bgcolor: t.color.pageHoverFill },
  },
  "& .MuiPaginationItem-root.Mui-focusVisible": {
    outline: "2px solid",
    outlineColor: "primary.main",
    outlineOffset: "-2px",
  },
  "& .MuiPaginationItem-root.Mui-selected": {
    bgcolor: t.color.ctaBg,
    color: t.color.onAccent,
    border: { xs: "none", md: "none" },
    fontWeight: t.weight.strong,
    "&:hover": { bgcolor: t.color.ctaBg, color: t.color.onAccent },
  },
  "& .MuiPaginationItem-ellipsis": {
    width: "auto",
    minWidth: "unset",
    px: t.space.paginationGap,
    height: t.size.pageItem,
    display: "flex",
    alignItems: "center",
    color: t.color.columnHeader,
    bgcolor: "transparent",
    fontSize: t.font.pageItem,
    fontWeight: t.weight.regular,
  },
  "& .MuiPaginationItem-icon": {
    width: t.size.pageArrow,
    height: t.size.pageArrow,
    fontSize: t.size.pageArrow,
  },
  "& .MuiPaginationItem-root.Mui-disabled": {
    color: t.color.pageDisabled,
    opacity: 1,
  },
};

/* ── Create / Edit Role form ─────────────────────────────────────────────── */

const f = t.form;

/** Card body: the two sections stacked with a rule between them. */
export const formBodySx: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  gap: f.sectionGap,
  p: f.cardPadding,
};

export const formSectionSx: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  gap: f.sectionBodyGap,
};

export const formSectionHeaderSx: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  gap: f.sectionHeaderGap,
};

export const formSectionTitleSx: SxProps<Theme> = {
  color: t.color.ink,
  fontSize: f.sectionTitleFontSize,
  fontWeight: f.sectionTitleWeight,
  letterSpacing: f.sectionTitleLetterSpacing,
  lineHeight: "normal",
};

export const formSectionDescriptionSx: SxProps<Theme> = {
  color: f.sectionDescriptionColor,
  fontSize: f.sectionDescriptionFontSize,
  lineHeight: f.sectionDescriptionLineHeight,
};

export const formDividerSx: SxProps<Theme> = {
  borderColor: f.divider,
};

export const formLabelSx: SxProps<Theme> = {
  mb: f.labelGap,
  color: f.labelColor,
  fontSize: f.labelFontSize,
  fontWeight: f.labelWeight,
  lineHeight: "normal",
  "& .MuiInputLabel-asterisk": { color: f.requiredColor },
};

/** Role name field — chrome shared with the selects below it. */
export const formTextFieldSx: SxProps<Theme> = {
  maxWidth: f.nameFieldMaxWidth,
  "& .MuiOutlinedInput-root": {
    height: f.fieldHeight,
    p: 0,
    borderRadius: f.fieldRadius,
    bgcolor: f.fieldBg,
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: f.fieldBorder,
      borderWidth: "1px",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: f.fieldBorder },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: f.fieldFocusBorder,
      borderWidth: "1px",
    },
    "&.Mui-error .MuiOutlinedInput-notchedOutline": { borderColor: "error.main" },
    "&.Mui-error.Mui-focused": {
      outline: "2px solid",
      outlineColor: "formField.focusBorder",
      outlineOffset: "2px",
    },
  },
  "& .MuiOutlinedInput-input": {
    height: "100%",
    boxSizing: "border-box",
    p: `0 ${f.fieldPaddingX}`,
    fontSize: f.fieldFontSize,
    fontWeight: f.fieldWeight,
    color: f.fieldColor,
    "&::placeholder": {
      color: f.fieldPlaceholder,
      opacity: 1,
      fontSize: f.fieldFontSize,
      fontWeight: f.fieldWeight,
    },
  },
  "& .MuiFormHelperText-root": {
    ml: 0,
    mt: "5px",
    fontSize: f.errorFontSize,
  },
};

/** One "resource ↔ permissions" pairing, plus its remove control. */
export const resourceCardSx: SxProps<Theme> = {
  display: "grid",
  gridTemplateColumns: {
    xs: "minmax(0, 1fr)",
    sm: `minmax(0, 1fr) minmax(0, 1fr) ${f.removeSize}px`,
  },
  alignItems: "start",
  columnGap: f.resourceColumnGap,
  rowGap: f.resourceRowGap,
  p: f.resourceRowPadding,
  bgcolor: f.resourceRowBg,
  border: `${t.border.hairline} solid ${f.resourceRowBorder}`,
  borderRadius: f.resourceRowRadius,
};

export const resourceListWrapperSx: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  gap: f.resourceRowGap,
};

/** MUI `Select` restyled to the same pill as the text field. */
export const formSelectSx: SxProps<Theme> = {
  width: "100%",
  height: f.fieldHeight,
  borderRadius: f.fieldRadius,
  bgcolor: f.fieldBg,
  fontSize: f.fieldFontSize,
  fontWeight: f.fieldWeight,
  color: f.fieldColor,
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: f.fieldBorder,
    borderWidth: "1px",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: f.fieldBorder },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: f.fieldFocusBorder,
    borderWidth: "1px",
  },
  "&.Mui-error .MuiOutlinedInput-notchedOutline": { borderColor: "error.main" },
  "&.Mui-error.Mui-focused": {
    outline: "2px solid",
    outlineColor: "formField.focusBorder",
    outlineOffset: "2px",
  },
  // Permissions stay closed until a resource is chosen; keep the same chrome
  // rather than MUI's washed-out disabled treatment.
  "&.Mui-disabled": {
    bgcolor: f.fieldBg,
    "& .MuiOutlinedInput-notchedOutline": { borderColor: f.fieldBorder },
    "& .MuiSelect-icon": { color: f.fieldBorder },
  },
  "& .MuiSelect-select": {
    display: "flex",
    alignItems: "center",
    height: "100%",
    boxSizing: "border-box",
    p: `0 ${f.fieldPaddingX}`,
    pr: "34px !important",
    minHeight: "unset",
  },
  "& .MuiSelect-icon": { right: 10, color: f.caretColor },
};

/** Truncates a long "add, update, delete" summary instead of wrapping it. */
export const selectValueSx: SxProps<Theme> = {
  fontSize: f.fieldFontSize,
  fontWeight: f.fieldWeight,
  color: f.fieldColor,
  textTransform: "capitalize",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

export const selectPlaceholderSx: SxProps<Theme> = {
  fontSize: f.fieldFontSize,
  fontWeight: f.fieldWeight,
  color: f.fieldPlaceholder,
};

export const selectOptionSx: SxProps<Theme> = {
  fontSize: f.fieldFontSize,
  fontWeight: f.fieldWeight,
  color: f.fieldColor,
  textTransform: "capitalize",
};

export const fieldErrorSx: SxProps<Theme> = {
  ml: 0,
  mt: "5px",
  fontSize: f.errorFontSize,
  color: "error.main",
};

/**
 * Grid cell holding the trash affordance. The top margin drops it past the
 * label above the selects so the button centres on the 40px field rather than
 * on the whole column.
 */
export const removeResourceWrapperSx: SxProps<Theme> = {
  display: "inline-flex",
  mt: { xs: 0, sm: `calc(${f.labelFontSize} * 1.2 + ${f.labelGap} + 2px)` },
  justifySelf: { xs: "flex-end", sm: "center" },
};

export const removeResourceSx: SxProps<Theme> = {
  width: f.removeSize,
  height: f.removeSize,
  p: 0,
  borderRadius: f.removeRadius,
  color: f.removeColor,
  "&:hover": { color: f.removeHoverColor, bgcolor: f.removeHoverBg },
  "&.Mui-disabled": { color: f.fieldBorder },
};

/**
 * Dashed row that closes the resource list. Fill and border sit under
 * `&.MuiButton-root` for the same reason the primary action does — the theme's
 * `text` variant forces `background: transparent` on hover.
 */
export const addResourceSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: f.addGap,
  width: "100%",
  height: f.addHeight,
  borderRadius: f.addRadius,
  fontSize: f.addFontSize,
  fontWeight: t.weight.strong,
  lineHeight: 1,
  textTransform: "none",
  "&.MuiButton-root": {
    background: "transparent",
    border: `${t.border.hairline} dashed ${f.addBorder}`,
    color: f.addColor,
    "&:hover": {
      background: f.addHoverBg,
      borderColor: f.addColor,
    },
  },
};

/** Right-aligned Cancel / Submit pair under a hairline. */
export const formFooterSx: SxProps<Theme> = {
  display: "flex",
  justifyContent: "flex-end",
  gap: f.footerGap,
  pt: f.footerPaddingTop,
  borderTop: `${t.border.hairline} solid ${f.divider}`,
};

export const secondaryButtonSx: SxProps<Theme> = {
  height: t.size.buttonHeight,
  minHeight: t.size.buttonHeight,
  px: t.size.buttonPaddingX,
  py: 0,
  borderRadius: t.radius.control,
  fontSize: t.font.button,
  fontWeight: t.weight.strong,
  lineHeight: 1,
  textTransform: "none",
  boxShadow: "none",
  "&.MuiButton-root": {
    background: f.secondaryBg,
    border: `${t.border.hairline} solid ${f.secondaryBorder}`,
    color: f.secondaryColor,
    "&:hover": {
      background: f.secondaryBg,
      borderColor: f.secondaryHoverBorder,
      color: f.secondaryHoverColor,
      boxShadow: "none",
    },
  },
};

/**
 * Filled "Create Role" action in the page header, and the form's Submit.
 *
 * The fill is pinned under `&.MuiButton-root`: the theme styles
 * `MuiButton.contained` with the `background` *shorthand*, which Emotion emits
 * after a plain `sx` `bgcolor` longhand and silently resets it back to the
 * palette primary. One extra specificity step is what makes the accent stick.
 */
export const createRoleButtonSx: SxProps<Theme> = {
  // Both small references run it full width under the search box.
  width: { xs: "100%", md: "auto" },
  height: { xs: r.action.height, md: t.size.buttonHeight },
  minHeight: { xs: r.action.height, md: t.size.buttonHeight },
  px: { xs: r.action.paddingX, md: t.size.buttonPaddingX },
  py: 0,
  border: "none",
  borderRadius: t.radius.control,
  fontSize: t.font.button,
  fontWeight: t.weight.strong,
  lineHeight: 1,
  textTransform: "none",
  boxShadow: "none",
  flexShrink: 0,
  whiteSpace: "nowrap",
  "&.MuiButton-root": {
    background: t.color.ctaBg,
    color: t.color.onAccent,
    boxShadow: "none",
    "&:hover": { background: t.color.ctaBgHover, boxShadow: "none" },
    "&.Mui-disabled": {
      background: t.color.ctaBg,
      opacity: 0.5,
      color: t.color.onAccent,
    },
  },
};

/* ---------------------------------------------------------------- card list */

/**
 * Below `md` a role is a card: its name and a drill-in chevron on one line,
 * then one labelled group of permission pills per resource.
 */
export const roleCardSx: SxProps<Theme> = {
  display: "flex",
  boxSizing: "border-box",
  flexDirection: "column",
  gap: r.card.gap,
  p: r.card.padding,
  width: "100%",
  bgcolor: t.color.surface,
  border: `${t.border.hairline} solid ${r.card.border}`,
  borderRadius: r.card.radius,
  textAlign: "left",
  font: "inherit",
  "&:focus-visible": {
    outline: `2px solid ${t.color.accent}`,
    outlineOffset: "2px",
  },
};

export const roleCardHeadSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  gap: r.card.headGap,
};

export const roleCardNameSx: SxProps<Theme> = {
  flex: 1,
  minWidth: 0,
  fontSize: r.card.nameFontSize,
  fontWeight: 800,
  color: t.color.ink,
  lineHeight: "normal",
  wordBreak: "break-word",
};

export const roleCardChevronSx: SxProps<Theme> = {
  width: r.card.chevronSize,
  height: r.card.chevronSize,
  flexShrink: 0,
  color: r.card.chevronColor,
};

export const roleCardGroupSx: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  gap: r.card.groupGap,
};

export const roleCardGroupLabelSx: SxProps<Theme> = {
  fontSize: r.card.labelFontSize,
  fontWeight: t.weight.strong,
  letterSpacing: r.card.labelLetterSpacing,
  color: t.color.columnHeader,
  textTransform: "uppercase",
  lineHeight: "normal",
};

export const roleCardPillsSx: SxProps<Theme> = {
  display: "flex",
  flexWrap: "wrap",
  gap: r.card.pillGap,
};

export const roleCardPillSx: SxProps<Theme> = {
  display: "inline-flex",
  alignItems: "center",
  fontSize: t.font.permission,
  fontWeight: t.weight.medium,
  color: t.color.permissionText,
  bgcolor: t.color.permissionBg,
  px: t.space.permissionPaddingX,
  py: r.card.pillPaddingY,
  borderRadius: t.radius.pill,
  lineHeight: "normal",
  whiteSpace: "nowrap",
};
